import React, { useEffect, useState } from "react";
import { FiPhone, FiCpu, FiHardDrive, FiServer, FiCalendar, FiRefreshCw } from "react-icons/fi";
import Loader from "../components/Loader";
import Calling from "../api/Calling";

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [totalNumber, setTotalNumber] = useState(0);
  const [serverLoad, setServerLoad] = useState({
    cpuLoad: "0",
    ramUsage: "0",
    storageUsage: "0"
  });
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardResp, serverResp] = await Promise.all([
        Calling.getAdminDashboard(),
        Calling.GetServerLoadCheck()
      ]);

      if (dashboardResp.status) {
        setTotalNumber(dashboardResp.totalMobiles);
      }

      if (serverResp.status) {
        setServerLoad({
          cpuLoad: serverResp.cpuLoad,
          ramUsage: serverResp.ramUsage,
          storageUsage: serverResp.storageUsage
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const serverResp = await Calling.GetServerLoadCheck();
      if (serverResp.status) {
        setServerLoad({
          cpuLoad: serverResp.cpuLoad,
          ramUsage: serverResp.ramUsage,
          storageUsage: serverResp.storageUsage
        });
      }
    } catch (error) {
      console.error("Error refreshing server load:", error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Get current date
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get status color based on percentage
  const getStatusColor = (value) => {
    const num = parseFloat(value);
    if (num < 50) return { bg: "bg-main-th", text: "text-main-blue", bar: "bg-main-blue" };
    if (num < 80) return { bg: "bg-orange-50", text: "text-orange-500", bar: "bg-orange-500" };
    return { bg: "bg-red-50", text: "text-main-delete", bar: "bg-main-delete" };
  };

  return (
    <>
      {loading ? (
        <Loader loader={loading} />
      ) : (
        <div className="w-full min-h-screen bg-main-bg pt-16">
          {/* Main Content */}
          <div className="p-4 md:p-6 lg:p-8 md:ml-[4.4rem] ">
            
            {/* Header Section */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-main-font">
                    Admin Dashboard
                  </h1>
                  <p className="text-main-subfont mt-1 text-sm md:text-base">
                    Welcome back! Here's your system overview.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-main-subfont bg-main-white px-4 py-2 rounded-xl border border-main-border">
                  <FiCalendar className="w-4 h-4 text-main-blue" />
                  <span className="text-sm">{currentDate}</span>
                </div>
              </div>
            </div>

            {/* Total Numbers Card */}
            <div className="mb-6">
              <div className="bg-main-white rounded-2xl border border-main-border p-6 max-w-sm">
                <div className="flex items-center gap-4">
                  <div className="p-4 rounded-xl bg-main-th">
                    <FiPhone className="w-7 h-7 text-main-blue" />
                  </div>
                  <div>
                    <p className="text-main-subfont text-sm font-medium">
                      Total Numbers
                    </p>
                    <p className="text-3xl font-bold text-main-font">
                      {totalNumber.toLocaleString()}
                    </p>
                    <p className="text-xs text-main-subfont mt-1">
                      All stored numbers
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Server Load Section */}
            <div className="bg-main-white rounded-2xl border border-main-border p-5 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-main-th">
                    <FiServer className="w-5 h-5 text-main-blue" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-main-font">Server Load</h2>
                    <p className="text-xs text-main-subfont">Real-time server statistics</p>
                  </div>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-main-blue bg-main-th hover:bg-main-thHover rounded-xl transition-colors duration-200 disabled:opacity-50"
                >
                  <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">Refresh</span>
                </button>
              </div>

              {/* Server Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                
                {/* CPU Load */}
                <div className="bg-main-bg rounded-xl p-5 border border-main-border/50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${getStatusColor(serverLoad.cpuLoad).bg}`}>
                        <FiCpu className={`w-5 h-5 ${getStatusColor(serverLoad.cpuLoad).text}`} />
                      </div>
                      <span className="font-medium text-main-font">CPU Load</span>
                    </div>
                    <span className={`text-2xl font-bold ${getStatusColor(serverLoad.cpuLoad).text}`}>
                      {serverLoad.cpuLoad}%
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-main-border/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${getStatusColor(serverLoad.cpuLoad).bar}`}
                      style={{ width: `${Math.min(parseFloat(serverLoad.cpuLoad), 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-main-subfont mt-3">Processor utilization</p>
                </div>

                {/* RAM Usage */}
                <div className="bg-main-bg rounded-xl p-5 border border-main-border/50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${getStatusColor(serverLoad.ramUsage).bg}`}>
                        <FiServer className={`w-5 h-5 ${getStatusColor(serverLoad.ramUsage).text}`} />
                      </div>
                      <span className="font-medium text-main-font">RAM Usage</span>
                    </div>
                    <span className={`text-2xl font-bold ${getStatusColor(serverLoad.ramUsage).text}`}>
                      {serverLoad.ramUsage}%
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-main-border/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${getStatusColor(serverLoad.ramUsage).bar}`}
                      style={{ width: `${Math.min(parseFloat(serverLoad.ramUsage), 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-main-subfont mt-3">Memory utilization</p>
                </div>

                {/* Storage Usage */}
                <div className="bg-main-bg rounded-xl p-5 border border-main-border/50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-lg ${getStatusColor(serverLoad.storageUsage).bg}`}>
                        <FiHardDrive className={`w-5 h-5 ${getStatusColor(serverLoad.storageUsage).text}`} />
                      </div>
                      <span className="font-medium text-main-font">Storage</span>
                    </div>
                    <span className={`text-2xl font-bold ${getStatusColor(serverLoad.storageUsage).text}`}>
                      {serverLoad.storageUsage}%
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-main-border/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${getStatusColor(serverLoad.storageUsage).bar}`}
                      style={{ width: `${Math.min(parseFloat(serverLoad.storageUsage), 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-main-subfont mt-3">Disk space utilization</p>
                </div>

              </div>

              {/* Status Indicator */}
              <div className="mt-6 pt-5 border-t border-main-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 bg-main-blue rounded-full animate-pulse"></div>
                    <span className="text-sm text-main-subfont">System Status: </span>
                    <span className="text-sm font-medium text-main-blue">Operational</span>
                  </div>
                  <p className="text-xs text-main-subfont">
                    Last updated: Just now
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default Home;